import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import jobService from '../../services/jobService';
import './JobDetail.css';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobDetails();
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await jobService.getJobById(id);
      setJob(response.data);
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error('Failed to load job details');
      navigate('/employer/my-jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/employer/jobs/${id}/edit`);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await jobService.deleteJob(id);
        toast.success('Job deleted successfully');
        navigate('/employer/my-jobs');
      } catch (error) {
        console.error('Error deleting job:', error);
        toast.error('Failed to delete job');
      }
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await jobService.updateJobStatus(id, newStatus);
      toast.success(`Job ${newStatus} successfully`);
      fetchJobDetails();
    } catch (error) {
      console.error('Error updating job status:', error);
      toast.error('Failed to update job status');
    }
  };

  if (loading) {
    return (
      <div className="emp-job-details">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="emp-job-details">
        <div className="no-jobs">
          <h3>Job not found</h3>
          <Link to="/employer/my-jobs" className="btn btn-primary">
            Back to My Jobs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="emp-job-details">
      {/* Header */}
      <div className="page-title-section">
        <div className="page-title-content">
          <div className="page-title-welcome">
            <div className="welcome-icon">
              <span className="material-symbols-outlined">work</span>
            </div>
            <div>
              <h1>Job Details</h1>
              <p>View and manage job information</p>
            </div>
          </div>
          <div className="header-actions-group">
            <Link to="/employer/my-jobs" className="btn btn-outline">
              <span className="material-symbols-outlined">arrow_back</span>
              Back to Jobs
            </Link>
            <button onClick={handleEdit} className="btn btn-primary">
              <span className="material-symbols-outlined">edit</span>
              Edit Job
            </button>
          </div>
        </div>
      </div>

      {/* Job Detail Content */}
      <div className="job-detail-container">
        {/* Main Info Card */}
        <div className="job-detail-card">
          <div className="job-detail-header">
            <div className="job-detail-title-section">
              <h2>{job.title}</h2>
              <div className="job-detail-badges">
                <span className={`status-badge status-${job.status}`}>
                  {job.status}
                </span>
                {job.featured && (
                  <span className="badge badge-featured">
                    <span className="material-symbols-outlined">star</span>
                    Featured
                  </span>
                )}
                {job.urgent && (
                  <span className="badge badge-urgent">
                    <span className="material-symbols-outlined">priority_high</span>
                    Urgent
                  </span>
                )}
              </div>
            </div>
            <div className="job-detail-actions">
              {job.status === 'active' && (
                <button 
                  onClick={() => handleStatusChange('closed')} 
                  className="btn btn-secondary"
                >
                  <span className="material-symbols-outlined">lock</span>
                  Close Job
                </button>
              )}
              {job.status === 'closed' && (
                <button 
                  onClick={() => handleStatusChange('active')} 
                  className="btn btn-success"
                >
                  <span className="material-symbols-outlined">lock_open</span>
                  Reopen Job
                </button>
              )}
              <button onClick={handleDelete} className="btn btn-danger">
                <span className="material-symbols-outlined">delete</span>
                Delete
              </button>
            </div>
          </div>

          {/* Company Info */}
          <div className="job-detail-section">
            <h3>
              <span className="material-symbols-outlined">business</span>
              Company Information
            </h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Company Name</label>
                <p>{job.company?.name || 'N/A'}</p>
              </div>
              {
                job.location?.city?.name && (
                  <div className="info-item">
                    <label>Location</label>
                    <p>{job.location?.city?.name}</p>
                  </div>
                )
              }
            </div>
          </div>

          {/* Job Details */}
          <div className="job-detail-section">
            <h3>
              <span className="material-symbols-outlined">description</span>
              Job Details
            </h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Job Type</label>
                <p>{job.jobType?.name || 'N/A'}</p>
              </div>
              <div className="info-item">
                <label>Experience Level</label>
                <p>{job.experienceLevel?.name || 'N/A'}</p>
              </div>
              <div className="info-item">
                <label>Salary Range</label>
                <p>
                  {job.salary?.min && job.salary?.max
                    ? `$${job.salary.min.toLocaleString()} - $${job.salary.max.toLocaleString()}`
                    : 'Not specified'}
                </p>
              </div>
              <div className="info-item">
                <label>Positions Available</label>
                <p>{job.positions || 1}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="job-detail-section">
            <h3>
              <span className="material-symbols-outlined">article</span>
              Job Description
            </h3>
            <div className="job-description-content">
              <p>{job.description || 'No description provided'}</p>
            </div>
          </div>

          {/* Requirements */}
          {job.requirements && (
            <div className="job-detail-section">
              <h3>
                <span className="material-symbols-outlined">checklist</span>
                Requirements
              </h3>
              <div className="job-description-content">
                <p>{job.requirements}</p>
              </div>
            </div>
          )}

          {/* Skills */}
          {job.skills && job.skills.length > 0 && (
            <div className="job-detail-section">
              <h3>
                <span className="material-symbols-outlined">psychology</span>
                Required Skills
              </h3>
              <div className="skills-list">
                {job.skills.map((skill, index) => (
                  <span key={index} className="skill-tag">
                    {skill.name || skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Statistics */}
          <div className="job-detail-section">
            <h3>
              <span className="material-symbols-outlined">analytics</span>
              Statistics
            </h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="material-symbols-outlined">visibility</span>
                <div>
                  <p className="stat-value">{job.views || 0}</p>
                  <p className="stat-label">Views</p>
                </div>
              </div>
              <div className="stat-item">
                <span className="material-symbols-outlined">people</span>
                <div>
                  <p className="stat-value">{job.applications || 0}</p>
                  <p className="stat-label">Applications</p>
                </div>
              </div>
              <div className="stat-item">
                <span className="material-symbols-outlined">calendar_today</span>
                <div>
                  <p className="stat-value">
                    {new Date(job.createdAt).toLocaleDateString()}
                  </p>
                  <p className="stat-label">Posted On</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="job-detail-sidebar">
          <div className="sidebar-card">
            <h3>Quick Actions</h3>
            <div className="sidebar-actions">
              <Link 
                to={`/employer/applications/${id}`} 
                className="sidebar-action-btn"
              >
                <span className="material-symbols-outlined">assignment</span>
                <div>
                  <p>View Applications</p>
                  <span>{job.applications || 0} applications</span>
                </div>
              </Link>
              <button 
                onClick={handleEdit} 
                className="sidebar-action-btn"
              >
                <span className="material-symbols-outlined">edit</span>
                <div>
                  <p>Edit Job</p>
                  <span>Update job details</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetail;
